import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../contexts';
import {
    convertUSDToCurrency,
    FALLBACK_EXCHANGE_RATES,
    formatCurrency,
    getCurrencyByLanguage,
    getLocaleByLanguage,
} from '../utils/currency';

interface ExchangeRates {
  [currency: string]: number;
}

interface UseCurrencyReturn {
  currency: string;
  locale: string;
  exchangeRates: ExchangeRates;
  isLoading: boolean;
  error: string | null;
  convertUSD: (amountUSD: number) => number;
  formatPrice: (amountUSD: number) => string;
  refreshRates: () => Promise<void>;
}

const EXCHANGE_RATE_API_URL = 'https://api.exchangerate-api.com/v4/latest/USD';
const CACHE_KEY = 'exchange_rates_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 horas em milissegundos

interface CachedRates {
  rates: ExchangeRates;
  timestamp: number;
}

/**
 * Hook para gerenciar conversão e formatação de moeda
 * Busca taxas de câmbio da API com cache de 24 horas
 */
export function useCurrency(): UseCurrencyReturn {
  const { currentLanguage } = useLanguage();
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>(
    FALLBACK_EXCHANGE_RATES
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currency = useMemo(
    () => getCurrencyByLanguage(currentLanguage),
    [currentLanguage]
  );
  const locale = useMemo(
    () => getLocaleByLanguage(currentLanguage),
    [currentLanguage]
  );

  /**
   * Carrega taxas de câmbio do cache ou da API
   */
  const loadExchangeRates = useCallback(async (): Promise<void> => {
    // Verificar cache primeiro
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      try {
        const cachedData: CachedRates = JSON.parse(cached);
        const now = Date.now();
        
        // Se o cache ainda é válido (menos de 24 horas)
        if (now - cachedData.timestamp < CACHE_DURATION) {
          setExchangeRates(cachedData.rates);
          return;
        }
      } catch (err) {
        // Se houver erro ao ler cache, continuar para buscar da API
        console.warn('Erro ao ler cache de taxas de câmbio:', err);
      }
    }

    // Buscar da API
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(EXCHANGE_RATE_API_URL);
      
      if (!response.ok) {
        throw new Error(`Erro ao buscar taxas de câmbio: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Converter formato da API para nosso formato
      // API retorna: { rates: { BRL: 5.0, EUR: 0.92, ... } }
      const rates: ExchangeRates = {
        USD: 1.0,
        ...data.rates,
      };
      
      setExchangeRates(rates);
      
      // Salvar no cache
      const cacheData: CachedRates = {
        rates,
        timestamp: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Erro ao buscar taxas de câmbio';
      setError(errorMessage);
      console.error('Erro ao buscar taxas de câmbio:', err);
      
      // Usar taxas de fallback em caso de erro
      setExchangeRates(FALLBACK_EXCHANGE_RATES);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Força atualização das taxas de câmbio
   */
  const refreshRates = useCallback(async (): Promise<void> => {
    // Limpar cache
    localStorage.removeItem(CACHE_KEY);
    await loadExchangeRates();
  }, [loadExchangeRates]);

  // Carregar taxas na montagem do componente
  useEffect(() => {
    loadExchangeRates();
  }, [loadExchangeRates]);

  /**
   * Converte valor de USD para a moeda local
   */
  const convertUSD = useCallback(
    (amountUSD: number): number => {
      const rate = exchangeRates[currency] || 1.0;
      return convertUSDToCurrency(amountUSD, currency, rate);
    },
    [currency, exchangeRates]
  );

  /**
   * Formata preço em USD para a moeda local formatada
   */
  const formatPrice = useCallback(
    (amountUSD: number): string => {
      const rate = exchangeRates[currency] || 1.0;
      const convertedAmount = convertUSDToCurrency(amountUSD, currency, rate);
      return formatCurrency(convertedAmount, currency, locale);
    },
    [currency, locale, exchangeRates]
  );

  return {
    currency,
    locale,
    exchangeRates,
    isLoading,
    error,
    convertUSD,
    formatPrice,
    refreshRates,
  };
}
